import {
  Box,
  Heading,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import assert from 'assert';
import _ from 'lodash';
import React from 'react';
import { Redirect } from 'react-router-dom';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';

import {
  BankAccountWithInstitutionAndDesignationGQLType,
  useGetFundingRequestWithBaseDetailsAndBorrowingBaseQuery,
  useGetUserQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { ApproveFundingRequestButton } from './ApproveButton';
import { colors } from 'packages/common-frontend/colors';
import { BackButton } from 'packages/common-frontend/components/BackButton';
import {
  errorToastConfig,
  fundingRequestKeyToLabel,
} from 'packages/common-frontend/constants';
import {
  bankAccountToDisplayString,
  dollarNumberWithCommas,
} from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { FundingRequestDetailBorrowingBaseSummary } from 'packages/funding-requests/ui/FundingRequestDetailBorrowingBaseSummary';
import { FundingRequestReports } from 'packages/funding-requests/ui/FundingRequestReports';
import {
  FundingRequestEventHistory,
  VerificationReportEventHistory,
} from 'packages/funding-requests/ui/shared/FundingRequestEvents';
import { StatusBadge } from 'packages/funding-requests/ui/shared/StatusBadge';
import { VerificationApproveTables } from 'packages/verifications/ui/VerificationApproveTables';

export const FundingRequestDetailViewContainer = ({
  fundingRequestId,
  creditFacilityId,
}: {
  fundingRequestId: string;
  creditFacilityId: string;
}) => {
  const toast = useToast();

  const { data: userData, loading: userLoading } = useGetUserQuery();
  const {
    data: fundingRequestData,
    loading: loadingFundingRequestData,
    error,
  } = useGetFundingRequestWithBaseDetailsAndBorrowingBaseQuery({
    variables: { fundingRequestId },
  });

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
    onError: () => {
      toast({
        ...errorToastConfig,
        id: 'errorGetCurrentCreditAgreementFullArtifact',
        title: 'Error loading credit agreement.',
      });
    },
  });

  if (
    !fundingRequestData ||
    loadingFundingRequestData ||
    !userData ||
    userLoading ||
    isCreditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.currentCreditAgreementFullArtifact
  ) {
    return <Skeleton data-testid="loading-funding-request" height="600px" />;
  }
  if (error) {
    return <Redirect to="/funding-requests" />;
  }

  const {
    fundingRequestWithFullDetails: {
      bankAccounts,
      created_at: createdAt,
      creditFacilityName,
      fundingRequestComponents,
      fundingRequestEvents,
      fundingRequestReports,
      status,
      verificationReport,
      verificationReportEvents,
      borrowingBaseReportId,
      borrowingBaseReport: { reportingDate },
    },
  } = fundingRequestData;
  assert(
    fundingRequestEvents,
    'Funding request events expected to exist for funding request with full details',
  );

  const bankAccountsMap = new Map<
    string,
    BankAccountWithInstitutionAndDesignationGQLType
  >(
    bankAccounts.map(
      (
        bankAccount: BankAccountWithInstitutionAndDesignationGQLType,
      ): [string, BankAccountWithInstitutionAndDesignationGQLType] => [
        bankAccount.id,
        bankAccount as BankAccountWithInstitutionAndDesignationGQLType,
      ],
    ),
  );

  const {
    currentCreditAgreementFullArtifact: {
      creditAgreement,
      id: creditAgreementId,
    },
  } = creditAgreementData;

  const parsedCreditAgreement = parseCreditAgreementFullArtifact(
    creditAgreement,
  );

  finleyAssert(
    parsedCreditAgreement,
    `Expect parsed currentCreditAgreementFullArtifact to exist for [creditFacilityId ${creditFacilityId}] [creditAgreementId: ${creditAgreementId}]`,
  );

  return (
    <>
      <Box height="100%">
        <Box display="flex" justifyContent="space-between">
          <Box>
            <Box display="flex" alignItems="center">
              <Heading as="h1" fontSize="4xl" paddingRight="10px">
                Funding Request
              </Heading>
              <StatusBadge value={status} />
            </Box>
            <Text color="gray">
              {commonDate.dateToHumanDateString(new Date(createdAt))}
            </Text>
          </Box>
          <BackButton />
        </Box>
        {fundingRequestData.fundingRequestWithFullDetails.status ===
          'submitted' && (
          <Box mt="25px">
            <ApproveFundingRequestButton
              fundingRequest={fundingRequestData.fundingRequestWithFullDetails}
            />
          </Box>
        )}
        <Box mt="35px">
          <Heading as="h3" fontSize="2xl" paddingTop="10px">
            Transaction Details
          </Heading>
          <Table variant="simple" mt="35px">
            <Thead
              background={colors.tableHeader}
              borderBottom="1px solid lightgrey"
              padding="8px 0"
            >
              <Tr>
                <Th textAlign="left" width="20%">
                  Transaction
                </Th>
                <Th textAlign="center" width="10%">
                  Amount
                </Th>
                <Th textAlign="center" width="20%">
                  Source
                </Th>
                <Th textAlign="center" width="20%">
                  Destination
                </Th>
                <Th textAlign="center" width="15%">
                  Funding Date
                </Th>
                <Th textAlign="center" width="15%">
                  Settled Date
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {_.map(
                fundingRequestComponents,
                ({
                  id,
                  type,
                  amount,
                  sourceBankAccountId,
                  destinationBankAccountId,
                  targetFundingDate,
                  postingDate,
                }) => {
                  const destinationBankAccount = bankAccountsMap.get(
                    destinationBankAccountId,
                  );
                  const sourceBankAccount = bankAccountsMap.get(
                    sourceBankAccountId,
                  );
                  return (
                    !!amount && (
                      <Tr key={`row-${id}`}>
                        <Td textAlign="left" width="20%">
                          {fundingRequestKeyToLabel[type]}
                        </Td>
                        <Td textAlign="center" width="10%">
                          {dollarNumberWithCommas(amount)}
                        </Td>
                        <Td textAlign="center" width="20%">
                          {bankAccountToDisplayString(sourceBankAccount)}
                        </Td>
                        <Td textAlign="center" width="20%">
                          {bankAccountToDisplayString(destinationBankAccount)}
                        </Td>
                        <Td textAlign="center" width="15%">
                          {targetFundingDate
                            ? commonDate.fullDateStrToHumanDateString(
                                targetFundingDate,
                              )
                            : '-'}
                        </Td>
                        <Td textAlign="center" width="15%">
                          {postingDate
                            ? commonDate.fullDateStrToHumanDateString(
                                postingDate,
                              )
                            : '-'}
                        </Td>
                      </Tr>
                    )
                  );
                },
              )}
            </Tbody>
          </Table>
          {fundingRequestReports && (
            <FundingRequestReports reports={fundingRequestReports} />
          )}
          {/* If verification report exists, evaluate to show Verification report.
          If not, show single column funding request borrowing base summary */}
          {verificationReport ? (
            (status === 'rejected' || status === 'approved') && (
              <Box pt="32px">
                <Heading as="h3" fontSize="2xl" pb="16px">
                  Borrowing Base Summary
                </Heading>
                <Box
                  borderWidth="2px"
                  borderRadius="8px"
                  padding="0 24px 24px 24px"
                  borderColor={colors.gray[200]}
                >
                  {/* @jendevelops [TODO-AssetVerification-postMVP]: add asset verification here, this is funding request detail page */}
                  <VerificationApproveTables
                    creditFacilityId={creditFacilityId}
                    creditAgreement={parsedCreditAgreement}
                    verificationReport={JSON.parse(
                      verificationReport.verificationReport,
                    )}
                    verificationReportId={
                      verificationReport.verificationReportId
                    }
                    verificationReportBorrowingBaseReportId={
                      verificationReport.borrowingBaseReportId
                    }
                    approvalReportNote={
                      verificationReport.approvalReportNote ?? undefined
                    }
                    selectedReportingDate={reportingDate}
                    viewMode="viewOnly"
                  />
                </Box>
              </Box>
            )
          ) : (
            <FundingRequestDetailBorrowingBaseSummary
              borrowingBaseReportId={borrowingBaseReportId}
              creditFacilityId={creditFacilityId}
            />
          )}
          <Heading as="h3" fontSize="2xl" paddingTop="10px" mt="35px" mb="25px">
            Event History
          </Heading>
          {fundingRequestEvents && creditFacilityName && (
            <FundingRequestEventHistory
              events={fundingRequestEvents}
              bankAccountsMap={bankAccountsMap}
              creditFacilityName={creditFacilityName}
            />
          )}
          {verificationReportEvents && creditFacilityName && (
            <VerificationReportEventHistory
              events={verificationReportEvents}
              creditFacilityName={creditFacilityName}
            />
          )}
        </Box>
      </Box>
    </>
  );
};
